import _                                                                            from 'underscore';
import React, { useState }                                                          from "react";
import { useMutation, useQuery }                                                    from "@apollo/react-hooks";
import { BasicDialog }                                                              from "../../../components/modals/BasicDialog";
import { Align, CorvalProjectVersion, SemanticUIColor, Size, Uuid }                 from "../../../../types";
import { BasicDialogContainer }                                                     from "../../../components/modals/BasicDialogContainer";
import { Icon, List }                                                               from "semantic-ui-react";
import { Cell, Grid }                                                               from "styled-css-grid";
import { StyledContainer }                                                          from "@src/views/containers";
import { UserQueries }                                                              from "@src/queries";
import { EditProjectVersionDialog }                                                 from "./EditProjectVersionDialog"; // Import the EditProjectVersionDialog
import { useSystemModals, useSystemToasts }                                         from "@src/hooks";
import { ProjectQueries }                                                           from "@src/queries/ProjectQueries";
import { FinePrintMessage }                                                         from "@src/views/components/util/messaging/FinePrintMessage";
import { InlineLoadingIndicatorLabel }                                              from "@src/views/components/util/loading/InlineLoadingIndicatorLabel";
import { UseIf }                                                                    from "@src/views/components/display/EqualValues";
import { DefaultProjectVersionLabel }                                               from '@src/views/components/project/DefaultProjectVersionLabel';
import { ActiveProjectVersionIndicator, ActiveProjectVersionIndicatorDefaultProps } from '@src/views/components/project/ActiveProjectVersionIndicator';


interface ManageProjectVersionsDialogProps {
    projectId: Uuid;
    title?: string;
}

function ManageProjectVersionsDialog( { projectId, title }: ManageProjectVersionsDialogProps ){


    const [ editVersion, setEditVersion ]                     = useState<CorvalProjectVersion | null>( null ),
          [ activeVersionUpdating, setActiveVersionUpdating ] = useState( false ),
          [ nextActiveVersionId, setNextActiveVersionId ]     = useState( null ),
          modalActions                                        = useSystemModals(),
          toastActions                                        = useSystemToasts();

    const { data: userData, loading: userLoading, error: userError, refetch } = useQuery( UserQueries.queries.GET_CURRENT_USER() );

    const [ deleteProjectVersion ]       = useMutation( ProjectQueries.mutations.DELETE_PROJECT_VERSION() ),
          [ updateActiveProjectVersion ] = useMutation( ProjectQueries.mutations.UPDATE_PROJECT_VERSION() ),
          currentProject                 = userData?.me?.projects?.find( p => p.project.id === projectId );

    let projectVersions = currentProject?.project.projectVersions || [];

    const handleEditProject = ( version: CorvalProjectVersion ) => {

        setEditVersion( version ); // Set the version to be edited

        modalActions.show(
            <EditProjectVersionDialog
                projectId={ projectId }
                projectVersionId={ version.id }
                currentVersionName={ version.name }
                currentVersionDescription={ version.description }
                currentVersionIsActive={ version.isActiveVersion }
                onClose={ () => modalActions.toggle( false ) }
                successToastLabel={ 'Project Version Updated Successfully' }
            />,
            {
                size:     'tiny',
                centered: false,
            }
        );
    };


    const handleToggleActiveVersion = async ( versionId: string, isActive: boolean ) => {

        const projectVersion = _.findWhere( projectVersions, { id: versionId } ) ?? null;

        if( !projectVersion ) return null;

        setNextActiveVersionId( versionId );
        setActiveVersionUpdating( true );

        await updateActiveProjectVersion( {
            variables: {
                input: {
                    projectVersionId: projectVersion.id,
                    name:             projectVersion.name,
                    description:      projectVersion.description,
                    isActiveVersion:  isActive,
                }
            }
        } );

        // manually update the active version so we don't need to refetch
        projectVersions = projectVersions.map( ( projectVersion: CorvalProjectVersion ) => {
            projectVersion.isActiveVersion = ( projectVersion.id === versionId );
        } );

        setActiveVersionUpdating( false );

        toastActions.success( 'Active Project Version Updated Successfully' );
    };


    const handleDeleteVersion = async ( versionId: string ) => {

        if( window.confirm( "Are you sure you want to delete this version?" ) ){

            try{
                await deleteProjectVersion( {
                    variables: {
                        input: {
                            projectVersionId: versionId
                        }
                    }
                } );

                // Refetch the data to update the list after deletion
                await refetch();

                toastActions.success( 'Project Version Deleted Successfully' );

            } catch( error ) {

                toastActions.error( 'There was an error deleting the project version' );

                console.error( "Error deleting project version:", error );
            }
        }
    };

    return (
        <BasicDialog showCancelButton={ false } showSubmitButton={ false } controlAlign={ Align.CENTER } className={ "no-padding" }>
            <BasicDialogContainer title={ title || "Manage Project Versions" } titleColor={ SemanticUIColor.PURPLE } contentPadding={ Size.MD }>
                <p>Our platform enables you to create and manage multiple versions of your commercialization maps. Each version can be tailored for different markets or strategies, allowing you to stay flexible and focused on your business goals</p>

                <StyledContainer padding={ [ Size.MD, null, Size.LG, null ] } textAlign={ Align.LEFT }>

                    <UseIf x={ userLoading } eq={ true }>
                        <StyledContainer textAlign={ Align.CENTER }>
                            <InlineLoadingIndicatorLabel label={ 'Loading Project Versions...' }/>
                        </StyledContainer>
                    </UseIf>

                    <List divided relaxed>
                        { projectVersions.map( ( version: CorvalProjectVersion ) => (

                            <List.Item key={ version.id }>

                                <Grid columns={ `50px 1fr auto auto` } rows={ `auto auto` } areas={ [ 'activeIndicator title edit delete', '. desc . .' ] }>

                                    <Cell area={ 'activeIndicator' } center>

                                        <ActiveProjectVersionIndicator
                                            isLoading={ activeVersionUpdating && ( nextActiveVersionId === version.id ) }
                                            isActive={ !activeVersionUpdating && version.isActiveVersion || ( activeVersionUpdating && nextActiveVersionId === version.id ) }
                                            onClick={ () => handleToggleActiveVersion( version.id, !version.isActiveVersion ) }
                                            inactivePopupMessage={ `${ ActiveProjectVersionIndicatorDefaultProps.inactivePopupMessage } Click to make this the active version.` }
                                        />

                                    </Cell>
                                    <Cell area={ 'title' } style={ { marginTop: 2 } }>
                                        <Grid columns={ `1fr auto` } className={ 'max-width padding-sm-right' }>
                                            <Cell center>
                                                <h4 className={ 'text-left' }>{ version.name } { version.isDefaultVersion }</h4>
                                            </Cell>
                                            <Cell center>

                                                { version.isDefaultVersion && <DefaultProjectVersionLabel popupProps={ { position: 'top right' } }/> }

                                            </Cell>
                                        </Grid>
                                    </Cell>
                                    <Cell area={ 'desc' }>
                                        { version.description && <FinePrintMessage>{ version.description }</FinePrintMessage> }
                                    </Cell>
                                    <Cell area={ 'edit' } style={ { marginTop: 3 } }>
                                        <Icon name="edit" onClick={ () => handleEditProject( version ) } style={ { cursor: "pointer", float: "right", marginRight: version.isDefaultVersion ? "19px" : "0px" } }/>
                                    </Cell>
                                    <Cell area={ 'delete' } style={ { marginTop: 3 } }>
                                        { !version.isDefaultVersion && (
                                            <Icon name="trash alternate" onClick={ () => handleDeleteVersion( version.id ) } style={ { cursor: "pointer" } }/>
                                        ) }

                                    </Cell>
                                </Grid>

                            </List.Item>

                        ) ) }
                    </List>

                </StyledContainer>
            </BasicDialogContainer>
        </BasicDialog>
    );
}

export { ManageProjectVersionsDialog };
